import { FunctionComponent, ReactNode } from "react";
import { getActiveSite } from "../selectors/sites.js";
import { toggleLegalNav } from "../slices/legalNav.js";
import { useAppDispatch, useAppSelector } from "../store/hooks.js";
import { Language, LegalNavId, PageLinkParams } from "../types/index.js";
import {
  checkIsOnePager,
  getFallbackLanguage,
  resolveLink,
} from "../utils/utils.js";
import SmartLink from "./SmartLink.js";

interface Props {
  legalNavId: LegalNavId;
  isPreview: boolean;
  languageId: Language;
  children?: ReactNode;
  target?: "_blank";
}

const LegalNavLink: FunctionComponent<Props> = ({
  legalNavId,
  isPreview,
  languageId,
  children,
}) => {
  const dispatch = useAppDispatch();

  const site = useAppSelector((state) => getActiveSite(state.sites));
  const isOnePager = checkIsOnePager(site);
  const pages = useAppSelector((state) => state.pages);
  const pageId = useAppSelector((state) => state.pages.byAlias[legalNavId]);

  if (!pageId) {
    throw new Error(`Page for legalNavId ${legalNavId} not found!`);
  }

  const pageLinkParams: PageLinkParams = {
    isPreview,
    languageId,
    pageId,
    pages,
    fallbackLanguageId: getFallbackLanguage(site, languageId),
  };

  const tmpAnchorAttrs = resolveLink(pageLinkParams);
  // Warning: don’t remove or change this id attribute.
  // It could be used in a DOM selector in Google Tag Manager.
  const id = `legal-nav-${legalNavId}`;

  const anchorAttrs = children
    ? {
        ...tmpAnchorAttrs,
        children,
        ...(isPreview ? {} : { target: "_blank", rel: "noopener" }),
        id,
      }
    : { ...tmpAnchorAttrs, id };

  return (
    <SmartLink
      anchorAttrs={
        isOnePager
          ? {
              ...anchorAttrs,
              onClick: (e) => {
                e.preventDefault();
                dispatch(toggleLegalNav({ legalNavId }));
              },
            }
          : anchorAttrs
      }
      isPreview={isPreview}
    />
  );
};

export default LegalNavLink;
